import { calculateCurrentValue } from './depreciation';

// A registry row normally stands for one item, but some are batches — fifty
// stacking chairs, twelve ceiling fans — recorded once with a quantity rather
// than fifty times over.
//
// `purchaseCost` is the cost of ONE unit. That was already true of every row
// written before quantity existed, since each of those was a single item, so
// reading cost as per-unit changes no existing figure.

export interface CountedAsset {
  quantity?: unknown;
  purchaseCost?: unknown;
  purchaseDate?: unknown;
  depreciationRate?: unknown;
  acquisitionType?: unknown;
}

/**
 * True when the row was gifted rather than bought. Anything other than an
 * explicit SPONSORED — including the missing value on every row written before
 * the field existed — is a purchase, so no old figure moves to the gift side.
 */
export const isSponsored = (asset?: CountedAsset | null): boolean =>
  asset?.acquisitionType === 'SPONSORED';

/**
 * Units a row covers, never less than 1.
 *
 * Rows saved before the column existed have no quantity, and a hand-edited or
 * imported row could hold anything. Every total on the Assets screen runs
 * through here, so a bad value must not be able to drag a sum to zero or
 * negative — it falls back to 1, which is what an unmarked row has always been.
 */
export const unitsOf = (asset?: CountedAsset | null): number => {
  const n = Number(asset?.quantity);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
};

/** What the row cost when bought: unit price times the units it covers. */
export const totalCostOf = (asset?: CountedAsset | null): number =>
  (parseFloat(String(asset?.purchaseCost ?? 0)) || 0) * unitsOf(asset);

/** Depreciated book value of every unit on the row. */
export const currentValueOf = (asset?: CountedAsset | null): number =>
  calculateCurrentValue(
    asset?.purchaseCost,
    asset?.purchaseDate,
    asset?.depreciationRate
  ).current * unitsOf(asset);

/**
 * Book value of a written-off row on the day it was written off.
 *
 * `currentValueOf` depreciates to today, so a register built on it would show
 * the loss shrinking every year after the fact. The write-off date is the row's
 * `deletedAt` (stored as e.g. "29 Sep 2026"); if it cannot be read, today is used.
 */
export const valueAtWriteOff = (asset?: (CountedAsset & { deletedAt?: unknown }) | null): number => {
  const on = new Date(String(asset?.deletedAt ?? ''));
  return calculateCurrentValue(
    asset?.purchaseCost,
    asset?.purchaseDate,
    asset?.depreciationRate,
    isNaN(on.getTime()) ? new Date() : on
  ).current * unitsOf(asset);
};

/**
 * Splitting a batch when part of it is written off. Returns what the surviving
 * row keeps and what the disposed row carries, or null when the request does
 * not make sense — more units than exist, a fraction, or nothing at all.
 *
 * `whole` marks the case where everything goes: there is nothing left to keep,
 * so the caller voids the row itself instead of splitting it.
 */
export interface WriteOffSplit {
  remaining: number;
  disposed: number;
  whole: boolean;
}

export const planWriteOff = (
  asset: CountedAsset | null | undefined,
  requested: unknown
): WriteOffSplit | null => {
  const owned = unitsOf(asset);
  const count = Number(requested);
  if (!Number.isInteger(count) || count < 1 || count > owned) return null;
  return { remaining: owned - count, disposed: count, whole: count === owned };
};
